import _ from 'lodash';
import { useMemo } from 'react';

import { checkWordTemplate } from 'core/helpers/text';

type TState = {
  cards: TWordUpdate[];
  errors: string[];
  isEmpty: boolean;
};

export default function useValidation(
  template: string,
  lang: TLang | null
): TState {
  const { cards, errors } = useMemo((): Omit<TState, 'isEmpty'> => {
    if (!template.trim()) {
      return { cards: [], errors: [] };
    }

    const blocks = template.replace(/^\s+$/gm, '').split(/\n\n/);
    const cards: TWordUpdate[] = [];
    const errors: string[] = [];

    for (const block of blocks) {
      try {
        cards.push(parseBlock(block, lang));
      } catch (error) {
        cards.push(ERROR_CARD);
        errors.push(String(error));
      }
    }

    return { cards, errors };
  }, [template, lang]);

  return {
    cards,
    errors,
    isEmpty: !template.trim() || errors.length + cards.length === 0,
  };
}

const parseBlock = (src: string, lang: TLang | null): TWordUpdate => {
  const [front, back] = src.split(/\s*\n\s*/);
  const frontLang = guessLang(front);
  const backLang = guessLang(back);

  // TODO: remove this hack
  if (backLang === TLang.ru && lang && lang !== TLang.ru) {
    throw new Error('Russian as the back side of the card. Are you sure?');
  }

  const [definition, note] = parseLine(front);
  const [value, valueNote] = parseLine(back);

  for (const n of [note, valueNote]) {
    if (n?.includes('{') || n?.includes('}')) {
      throw new Error(`Notes do not support highlights: "${n}"`);
    }
  }

  return genCard({ frontLang, definition, note, value, valueNote });
};

// TODO: remove this hack
const guessLang = (src: string): TLang => {
  if (/[А-ЯЁа-яё]/.test(src)) {
    return TLang.ru;
  }
  return /[öüßä]/m.test(src) ? TLang.de : TLang.en;
};

const parseLine = (src: string): [string, string | null] => {
  const [, value, note] = src.match(/^(.+?)(?:\(([^()]+)\))?$/) ?? [];
  if (!value) {
    throw new Error(`Incorrect line: "${src}"`);
  }

  if (!checkWordTemplate(src)) {
    throw new Error(`Incorrect highlights in this line: "${src}"`);
  }

  return [normalize(value), note ? normalize(note) : null];
};

const normalize = (src: string): string =>
  _.trim(src.replace(/\s{2,}/g, ' '), '. \t').normalize();

type TOpts = {
  definition: string;
  note: string | null;
  value: string;
  valueNote: string | null;
  frontLang: TLang;
};
const genCard = (opts: TOpts): TWordUpdate => ({
  definitions: {
    [opts.frontLang]: opts.definition,
  },
  note: opts.note,
  value: opts.value,
  valueNote: opts.valueNote,
  // empty fields
  tags: [],
  image: null,
});

const ERROR_CARD = genCard({
  value: 'ERROR',
  frontLang: TLang.en,
  definition: 'ERROR',
  valueNote: null,
  note: null,
});
